import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert, Navbar, Nav } from 'react-bootstrap';
import {
  PublicUserProfile,
  authService,
  User,
  friendshipService,
  loadUnreadNotificationCount,
} from '../services/api';
import AddFriendButton from './AddFriendButton';
import FriendsList from './FriendsList';
import PendingRequests from './PendingRequests';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import LanguageSelector from './LanguageSelector';
import ProfileAchievementsSection from './ProfileAchievementsSection';
import NavbarBrandLogo from './NavbarBrandLogo';
import UserNavbarMenus from './UserNavbarMenus';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import PwaInstallButton from './PwaInstallButton';
import { isVipSubscriber } from '../utils/isVipSubscriber';
import './PublicProfile.css';

const PublicProfile: React.FC = () => {
  const { t } = useSafeTranslation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<PublicUserProfile | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  useEffect(() => {
    if (userId) {
      loadPublicProfile(userId);
    }
    loadCurrentUser();
  }, [userId]);

  useEffect(() => {
    if (currentUser) {
      loadPendingRequestsCount();
    }
  }, [currentUser]);

  const loadCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const userData = await authService.getProfile();
        setCurrentUser(userData);
        setUnreadNotificationCount(await loadUnreadNotificationCount(userData));
      }
    } catch (err) {
      // User not logged in, that's fine
    }
  };

  const loadPublicProfile = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const profileData = await authService.getPublicProfile(id);
      setProfile(profileData);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('User not found');
      } else {
        setError('Failed to load profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadPendingRequestsCount = async () => {
    try {
      const requests = await friendshipService.getPendingRequests();
      setPendingRequestsCount(requests.length);
    } catch {
      /* ignore */
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    window.location.href = '/';
  };

  const handleRequestResponded = () => {
    loadPendingRequestsCount();
  };

  const getUserTypeBadge = (userType: string | number) => {
    switch (userType) {
      case 'Admin':
      case 3:
        return <Badge bg="danger">Admin</Badge>;
      case 'Vip':
      case 'Premium':
      case 2:
        return <Badge bg="warning">VIP</Badge>;
      case 'Free':
      case 1:
        return <Badge bg="secondary">Free</Badge>;
      default:
        return <Badge bg="secondary">Free</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="profile-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile-page">
        <Container fluid className="home-content">
          <Row className="justify-content-center">
            <Col xs={12} md={8} lg={6}>
              <div className="text-center py-5">
                <Alert variant="danger">
                  <Alert.Heading>{t('common.error', 'Error')}</Alert.Heading>
                  <p>{error || t('user.profileNotFound', 'Profile not found')}</p>
                  <Button variant="outline-danger" onClick={handleBackToHome}>
                    <i className="bi bi-arrow-left me-2"></i>
                    {t('navigation.backToHome', 'Back to Home')}
                  </Button>
                </Alert>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Navbar - Same as HomePage and UserProfile */}
      {currentUser && (
        <Navbar bg="white" expand="lg" className="home-navbar" collapseOnSelect>
          <Container fluid>
            <Navbar.Brand
              className="navbar-brand d-flex align-items-center"
              onClick={() => (window.location.href = '/')}
              style={{ cursor: 'pointer' }}
              aria-label="Quizter Club"
            >
              <NavbarBrandLogo />
              <span className="d-none d-lg-inline">Quizter Club</span>
            </Navbar.Brand>

            <div className="d-flex d-lg-none align-items-center ms-auto gap-1 gap-sm-2 navbar-mobile-toolbar">
              <PwaInstallButton className="flex-shrink-0" iconOnly />
              <LanguageSelector isInline={true} />
              <NavbarMusicToggle className="flex-shrink-0" />
              <NotificationBell
                className="d-lg-none"
                unreadCount={unreadNotificationCount}
                onUnreadCountChange={setUnreadNotificationCount}
              />
              <Navbar.Toggle aria-controls="basic-navbar-nav" />
            </div>
            <Navbar.Collapse id="basic-navbar-nav" className="justify-content-lg-end">
              <Nav className="flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 justify-content-lg-end">
                <div className="d-none d-lg-flex align-items-center gap-2 me-lg-3">
                  <PwaInstallButton />
                  <LanguageSelector isInline={true} />
                </div>
                <UserNavbarMenus
                  user={currentUser}
                  pendingRequestsCount={pendingRequestsCount}
                  unreadNotificationCount={unreadNotificationCount}
                  onUnreadNotificationCountChange={setUnreadNotificationCount}
                  onOpenFriends={() => setShowFriendsList(true)}
                  onOpenPending={() => setShowPendingRequests(true)}
                  onLogout={handleLogout}
                />
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      )}

      {/* Main Content */}
      <Container fluid className="home-content">
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6}>
            <Card className="profile-card">
              <Card.Body className="text-center p-4">
                <div className="profile-avatar mb-4">
                  <i className="bi bi-person-circle"></i>
                </div>
                
                <h2
                  className={`profile-nickname mb-3${
                    isVipSubscriber(profile.userType) ? ' vip-subscriber-nickname' : ''
                  }`}
                >
                  {profile.nickname}
                  {isVipSubscriber(profile.userType) && (
                    <i className="bi bi-gem text-warning ms-1" title="VIP" aria-hidden />
                  )}
                </h2>
                
                <div className="profile-badges mb-4">
                  {getUserTypeBadge(profile.userType)}
                </div>

                <div className="profile-stats">
                  <Row className="g-2 profile-stats-row">
                    <Col xs={6}>
                      <div className="stat-item">
                        <div className="stat-value">{profile.level}</div>
                        <div className="stat-label">{t('user.level', 'Level')}</div>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="stat-item">
                        <div className="stat-value">{profile.experiencePoints.toLocaleString()}</div>
                        <div className="stat-label">{t('user.experiencePoints', 'Experience Points')}</div>
                      </div>
                    </Col>
                  </Row>
                </div>

                <ProfileAchievementsSection
                  variant="public"
                  userId={profile.id}
                  className="mt-3"
                  sharePreviewPlayerName={profile.nickname}
                />

                {currentUser && currentUser.id !== profile.id && (
                  <div className="profile-actions mt-4">
                    <AddFriendButton 
                      targetUserId={profile.id}
                      currentUserId={currentUser.id}
                    />
                  </div>
                )}

                <div className="d-flex gap-3 justify-content-center mt-4">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleBackToHome}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    {t('navigation.backToHome', 'Back to Home')}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modals */}
      {showFriendsList && (
        <FriendsList 
          onClose={() => setShowFriendsList(false)} 
        />
      )}

      {showPendingRequests && (
        <PendingRequests 
          onClose={() => setShowPendingRequests(false)}
          onRequestResponded={handleRequestResponded}
        />
      )}
    </div>
  );
};

export default PublicProfile; 